import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const GLOBAL_TIMEOUT_EVENT = 'react-global-timeout-error';

/**
 * Catches uncaught runtime errors (e.g. ReCAPTCHA "Timeout", chunk load failures)
 * and shows a friendly fallback instead of the React error overlay.
 * Also listens for unhandled promise rejections (e.g. "Timeout") so they show
 * the same friendly UI instead of the dev overlay.
 */
class ErrorBoundary extends Component<Props, State> {
  private unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
    const message =
      typeof event.reason?.message === 'string'
        ? event.reason.message
        : String(event.reason ?? '');
    if (
      message === 'Timeout' ||
      message.toLowerCase().includes('timeout') ||
      message.toLowerCase().includes('network')
    ) {
      event.preventDefault();
      this.setState({ hasError: true, error: new Error(message || 'Timeout') });
    }
  };

  private globalTimeoutHandler = () => {
    this.setState({ hasError: true, error: new Error('Timeout') });
  };

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidMount() {
    window.addEventListener('unhandledrejection', this.unhandledRejectionHandler);
    window.addEventListener(GLOBAL_TIMEOUT_EVENT, this.globalTimeoutHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
    window.removeEventListener(GLOBAL_TIMEOUT_EVENT, this.globalTimeoutHandler);
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const { message } = this.state.error;
      const isTimeout =
        message === 'Timeout' ||
        message.toLowerCase().includes('timeout') ||
        message.toLowerCase().includes('network');

      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            backgroundColor: 'var(--bg-app)',
          }}
        >
          <Paper
            sx={{
              maxWidth: 460,
              p: 4.5,
              textAlign: 'center',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-xl)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <Typography variant="h5" color="error.main" gutterBottom fontWeight={700}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {isTimeout
                ? 'A request timed out. This can happen if the server is slow, reCAPTCHA failed to load, or your connection is unstable. Try refreshing.'
                : message || 'An unexpected error occurred.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleRetry}
            >
              Refresh page
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
